import { delinearize, IGroundRaster, linearize } from './labelGroundTone'
import { IPixelRect } from './rasterTileMath'

// Reduces a probe patch of RGBA bytes to the one colour the label stands on.
//
// Two different spaces, deliberately: the spatial average is LINEAR, because the eye integrates
// light over an area and light is linear — averaging bytes instead turns half-black-half-white
// into a much darker grey, which flips the ink on a mixed roof. Alpha compositing stays GAMMA,
// left to `over()`, because that is where the GPU blended it.
//
// The mean is alpha-WEIGHTED, which makes average-then-composite equal composite-then-average
// for a fixed backdrop, and disposes of the transparent-pixel trap for free: canvas
// unpremultiplication reports a transparent pixel as black, and alpha keeps it out.
// Rec. 709 luminance of an already-linear triple.
const luminance = (r: number, g: number, b: number): number =>
  0.2126 * r + 0.7152 * g + 0.0722 * b

// Spread of linear luminance over the same alpha-weighted population as the mean, so the two
// describe the same pixels. Clamped at zero: the one-pass form can land a hair below it on a
// perfectly flat patch.
const stdDev = (sumL: number, sumL2: number, sumA: number): number => {
  const mean = sumL / sumA
  return Math.sqrt(Math.max(0, sumL2 / sumA - mean * mean))
}

export function averageRgba(data: Uint8ClampedArray): IGroundRaster {
  let sumA = 0
  let r = 0
  let g = 0
  let b = 0
  let sumL = 0
  let sumL2 = 0
  const count = data.length / 4
  if (!count) return { rgb: [0, 0, 0], alpha: 0, contrast: 0 }

  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3] / 255
    if (!a) continue
    sumA += a
    const lr = linearize(data[i])
    const lg = linearize(data[i + 1])
    const lb = linearize(data[i + 2])
    r += a * lr
    g += a * lg
    b += a * lb
    const lum = luminance(lr, lg, lb)
    sumL += a * lum
    sumL2 += a * lum * lum
  }

  const alpha = sumA / count
  if (!sumA) return { rgb: [0, 0, 0], alpha: 0, contrast: 0 }

  return {
    rgb: [
      Math.round(delinearize(r / sumA)),
      Math.round(delinearize(g / sumA)),
      Math.round(delinearize(b / sumA)),
    ],
    alpha,
    contrast: stdDev(sumL, sumL2, sumA),
  }
}

// The same average over a sub-rect of a larger buffer, so a source that has been read back
// once can answer many cells without touching a canvas again. Extracting each rect into its
// own array first would allocate per cell; this walks the rows in place.
// A cell's rect grows as the camera pulls back, so sampling every pixel let one screenful of
// labels ask for tens of millions. A regular grid estimates the same mean — this is a mean, not
// a search for a rare pixel — and bounds the work per cell.
const MAX_SAMPLES_PER_RECT = 1024

export function averageRgbaRect(
  data: Uint8ClampedArray,
  stride: number,
  rect: IPixelRect,
): IGroundRaster {
  const step = Math.max(
    1,
    Math.ceil(Math.sqrt((rect.width * rect.height) / MAX_SAMPLES_PER_RECT)),
  )
  let sumA = 0
  let r = 0
  let g = 0
  let b = 0
  let sumL = 0
  let sumL2 = 0
  let count = 0
  for (let y = rect.y; y < rect.y + rect.height; y += step) {
    let i = (y * stride + rect.x) * 4
    for (let x = 0; x < rect.width; x += step, i += 4 * step) {
      const alpha = data[i + 3] / 255
      const lr = linearize(data[i])
      const lg = linearize(data[i + 1])
      const lb = linearize(data[i + 2])
      r += lr * alpha
      g += lg * alpha
      b += lb * alpha
      const lum = luminance(lr, lg, lb)
      sumL += alpha * lum
      sumL2 += alpha * lum * lum
      sumA += alpha
      count++
    }
  }
  if (!count || !sumA) return { rgb: [0, 0, 0], alpha: 0, contrast: 0 }
  return {
    rgb: [
      Math.round(delinearize(r / sumA)),
      Math.round(delinearize(g / sumA)),
      Math.round(delinearize(b / sumA)),
    ],
    alpha: sumA / count,
    // Read off the SAME subsampled grid as the mean, so the two agree — but note the grid
    // undersamples: 1px drafting strokes on a rect sampled every other pixel are partly
    // stepped over, so this reads LOW on exactly the content it exists to detect. The
    // threshold it feeds is therefore measured against real sheets, never derived.
    contrast: stdDev(sumL, sumL2, sumA),
  }
}
